import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { STAGES, SOURCES, SOURCE_META, PRIORITY_META, DIAMETERS, TUBE_TYPES, stageLabel, sourceLabel } from "@/lib/funnel";
import { StageBadge } from "@/components/StageBadge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("all");
  const [stage, setStage] = useState("all");
  const [assigned, setAssigned] = useState("all");
  const [city, setCity] = useState("");
  const [diameter, setDiameter] = useState("all");
  const [tubeType, setTubeType] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const view = params.get("view") || undefined;

  const load = async () => {
    setLoading(true);
    const q = {};
    if (search) q.search = search;
    if (source !== "all") q.source = source;
    if (stage !== "all") q.stage = stage;
    if (assigned !== "all") q.assigned_to = assigned;
    if (city) q.city = city;
    if (diameter !== "all") q.diameter = diameter;
    if (tubeType !== "all") q.tube_type = tubeType;
    if (view) q.view = view;
    const { data } = await api.get("/leads", { params: q });
    setLeads(data);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, source, stage, assigned, city, diameter, tubeType, view]);

  return (
    <div data-testid="leads-page">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-semibold text-slate-900">All Leads</h1>
        <p className="text-slate-500 text-sm">{leads.length} leads</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="leads-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company, contact, phone, product…"
            className="pl-9 bg-white"
          />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-full sm:w-40 bg-white" data-testid="filter-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {SOURCES.map((s) => <SelectItem key={s} value={s}>{SOURCE_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-full sm:w-44 bg-white" data-testid="filter-stage"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {STAGES.map((s) => <SelectItem key={s} value={s}>{stageLabel(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assigned} onValueChange={setAssigned}>
          <SelectTrigger className="w-full sm:w-40 bg-white" data-testid="filter-assignee"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Owners</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={diameter} onValueChange={setDiameter}>
          <SelectTrigger className="w-full sm:w-32 bg-white" data-testid="filter-diameter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Dia</SelectItem>
            {DIAMETERS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tubeType} onValueChange={setTubeType}>
          <SelectTrigger className="w-full sm:w-32 bg-white" data-testid="filter-tubetype"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {TUBE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          data-testid="filter-city"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="City…"
          className="w-full sm:w-36 bg-white"
        />
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <th className="text-left font-medium py-3 px-4">Company</th>
              <th className="text-left font-medium py-3 px-4">Source</th>
              <th className="text-left font-medium py-3 px-4">Stage</th>
              <th className="text-left font-medium py-3 px-4">Priority</th>
              <th className="text-left font-medium py-3 px-4">Owner</th>
              <th className="text-left font-medium py-3 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr
                key={l.id}
                onClick={() => navigate(`/leads/${l.id}`)}
                data-testid={`lead-row-${l.id}`}
                className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
              >
                <td className="py-3 px-4">
                  <p className="font-medium text-slate-900">{l.company_name}</p>
                  <p className="text-xs text-slate-500">{l.product_requirement || "—"}</p>
                  {(l.diameter || l.tube_type || l.city) && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {l.diameter && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">{l.diameter}</span>}
                      {l.tube_type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-medium">{l.tube_type}</span>}
                      {l.city && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">{l.city}</span>}
                    </div>
                  )}
                </td>
                <td className="py-3 px-4">
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${SOURCE_META[l.source]?.color}`}>
                    {sourceLabel(l)}
                  </span>
                </td>
                <td className="py-3 px-4"><StageBadge stage={l.stage} /></td>
                <td className="py-3 px-4">
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${PRIORITY_META[l.priority]?.color}`}>
                    {PRIORITY_META[l.priority]?.label}
                  </span>
                </td>
                <td className="py-3 px-4 text-slate-600">{l.assigned_to_name || "—"}</td>
                <td className="py-3 px-4">
                  {l.overdue ? (
                    <span className="text-xs px-2 py-0.5 rounded font-bold bg-red-100 text-red-700">Overdue</span>
                  ) : (
                    <span className="text-xs text-slate-400">On track</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && leads.length === 0 && (
          <p className="text-center text-slate-400 py-12 text-sm">No leads match your filters.</p>
        )}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {leads.map((l) => (
          <div
            key={l.id}
            onClick={() => navigate(`/leads/${l.id}`)}
            data-testid={`lead-card-${l.id}`}
            className={`bg-white border ${l.overdue ? "border-red-300" : "border-slate-200"} rounded-lg p-4 shadow-sm`}
          >
            <div className="flex justify-between items-start">
              <p className="font-medium text-slate-900">{l.company_name}</p>
              <StageBadge stage={l.stage} />
            </div>
            <p className="text-xs text-slate-500 mt-1">{l.product_requirement || "—"}</p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${SOURCE_META[l.source]?.color}`}>
                {sourceLabel(l)}
              </span>
              <span className="text-xs text-slate-500">{l.assigned_to_name || "Unassigned"}</span>
              {l.overdue && <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-red-100 text-red-700">Overdue</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
